import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import HelpDialog from '@/components/HelpDialog';
import { User, Menu, Plus } from 'lucide-react';

const ACTIVE_TASKS_HELP = `**This page displays all tasks that are your "current focus".** How you define "current" is up to you — this could be your "Today" list, these could be the tasks you want to get done this week, or this could be a rolling list that you constantly add to as you complete tasks.

There are **4 ways a task can be added to this list:**

- When creating a task, select "Mark as Active" in the task creation window.
- In Storage, click the little moon icon on the right side of a task card. This switches the icon to a sun and the task becomes active.
- Click the "<u>Auto Fill</u>" button in the top right of the Active Tasks page; this automatically populates your active list with the highest priority tasks in Storage (while ignoring any tasks that have been assigned a future active date). By default, Auto Fill brings your active list up to 7 tasks (you can change this number in Settings).
- If you have assigned an "Active Date" to a task, that task will automatically get added to the active list on that date.

A key strategy to using the active list well is to **pre-arrange your tasks in the order that you want to complete them** (with the highest priority tasks at the top). That way, whenever you complete a task and come back to your list, you don't have to waste mental energy deciding which task to work on next — you have already made that decision for yourself! Just start at the top and work your way down. By default, tasks are sorted on this page according tag priority order; however, you can **manually re-order tasks via drag-and-drop.**

In the upper right corner, there are some **other helpful buttons**:

- <u>Focus Mode</u>: This hides all tasks except the top five from the list, with visual prominence given to the top task. Whenever you complete a task, all other tasks below it will shift up to take its place. Work your way down the list, one task at a time!
- <u>Re-Sort</u>: This removes any manual ordering from tasks in the list and re-sorts them according to tag priority order.
- <u>Clear All</u>: This clears all tasks of their active status, thus removing them from the active list.

**One more tip:** if you want to "push back" a task to be active at a later date, simply change its assigned active date to be that future date. This will automatically remove the task from the active list; it will get re-added once its new active date arrives.`;

const SECONDARY_BTN_STYLE = {
  borderWidth: '1px',
  borderStyle: 'solid',
  backgroundColor: 'var(--color-secondary)',
};

const FOCUS_BTN_STYLE = {
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderWidth: '1px',
  borderStyle: 'solid',
};

/** Inline filter set used in both desktop & mobile bars */
function FilterAndAddListGroup({
  assigneeTagsEnabled,
  assigneeTags,
  assigneeFilter,
  setAssigneeFilter,
  contextsEnabled,
  contexts,
  contextFilter,
  setContextFilter,
  handleAddNewList,
  testIdSuffix = '',
}) {
  return (
    <>
      {assigneeTagsEnabled && assigneeTags.length > 0 && (
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger
            className="h-8 w-auto px-3 text-sm"
            data-testid={`assignee-filter-active${testIdSuffix}`}
            style={SECONDARY_BTN_STYLE}
          >
            <User size={14} className="mr-1.5" />
            <span>
              {assigneeFilter === 'any-user'
                ? 'Any User'
                : assigneeFilter.replace(/^[^\s]+\s/, '')}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any-user">Any User</SelectItem>
            {assigneeTags.map((tag) => (
              <SelectItem key={tag.id} value={`${tag.emoji} ${tag.name}`}>
                {tag.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {contextsEnabled && contexts.length > 0 && (
        <Select value={contextFilter} onValueChange={setContextFilter}>
          <SelectTrigger
            className="h-8 w-auto px-3 text-sm"
            data-testid={`context-filter-active${testIdSuffix}`}
            style={SECONDARY_BTN_STYLE}
          >
            <SelectValue>
              {contextFilter === 'any-context'
                ? 'Any Context'
                : contexts.find((c) => c.id === contextFilter)?.name || 'Any Context'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any-context">Any Context</SelectItem>
            {contexts.map((context) => (
              <SelectItem key={context.id} value={context.id}>
                {context.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button
        onClick={handleAddNewList}
        variant="secondary"
        size="sm"
        data-testid={`add-active-list-button${testIdSuffix}`}
        className="hover:!text-black"
        style={SECONDARY_BTN_STYLE}
      >
        <Plus size={14} className="mr-px" />
        Add New List
      </Button>
    </>
  );
}

/**
 * ActiveActionBar
 * Page header (title + help) and the row of action buttons + filters
 * that appears at the top of the Active page on both desktop and mobile.
 */
export default function ActiveActionBar({
  // State + setters
  focusMode,
  setFocusMode,
  mobileMenuOpen,
  setMobileMenuOpen,
  assigneeFilter,
  setAssigneeFilter,
  contextFilter,
  setContextFilter,
  // Data
  activeTasksCount,
  assigneeTagsEnabled,
  assigneeTags,
  contextsEnabled,
  contexts,
  hasMultipleLists,
  // Handlers
  handleAutoPopulate,
  handleReSort,
  handleClearAll,
  handleAddNewList,
}) {
  // When 2+ lists, Auto Fill / Re-Sort / Clear All move into each list's
  // kabab menu, and the Context filter is hidden from the top action bar.
  const showListActions = !hasMultipleLists;
  const showContextFilter = !hasMultipleLists;

  const filtersProps = {
    assigneeTagsEnabled,
    assigneeTags,
    assigneeFilter,
    setAssigneeFilter,
    contextsEnabled: contextsEnabled && showContextFilter,
    contexts,
    contextFilter,
    setContextFilter,
    handleAddNewList,
  };

  return (
    <header className="max-w-3xl mx-auto px-4 sm:px-6">
      {/* Desktop */}
      <div className="hidden md:block">
        <div className="flex items-center gap-2">
          <h1 className="h-font text-2xl md:text-3xl font-semibold text-white">
            Active Tasks
          </h1>
          <HelpDialog title="Active Tasks Guide" content={ACTIVE_TASKS_HELP} size="large" />
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3">
          {activeTasksCount > 0 && (
            <Button
              onClick={() => setFocusMode(!focusMode)}
              variant="secondary"
              size="sm"
              data-testid="focus-mode-button"
              style={focusMode ? FOCUS_BTN_STYLE : SECONDARY_BTN_STYLE}
            >
              {focusMode ? 'Exit Focus' : 'Focus Mode'}
            </Button>
          )}
          {showListActions && (
            <>
              <Button
                onClick={handleAutoPopulate}
                variant="secondary"
                size="sm"
                data-testid="auto-populate-button"
                style={SECONDARY_BTN_STYLE}
              >
                Auto Fill
              </Button>
              <Button
                onClick={handleReSort}
                variant="secondary"
                size="sm"
                data-testid="resort-button"
                style={SECONDARY_BTN_STYLE}
              >
                Re-Sort
              </Button>
              <Button
                onClick={handleClearAll}
                variant="secondary"
                size="sm"
                data-testid="clear-all-button"
                style={SECONDARY_BTN_STYLE}
              >
                Clear All
              </Button>
            </>
          )}
          <FilterAndAddListGroup {...filtersProps} />
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="h-font text-2xl font-semibold text-white">Active Tasks</h1>
            <HelpDialog title="Active Tasks Guide" content={ACTIVE_TASKS_HELP} size="large" />
          </div>
          <div className="flex items-center gap-2">
            {activeTasksCount > 0 && (
              <Button
                onClick={() => setFocusMode(!focusMode)}
                variant="secondary"
                size="sm"
                data-testid="focus-mode-button-mobile"
                style={focusMode ? FOCUS_BTN_STYLE : SECONDARY_BTN_STYLE}
              >
                {focusMode ? 'Exit Focus' : 'Focus Mode'}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-300 hover:text-black hover:bg-slate-200"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={24} />
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="mt-3 flex flex-wrap gap-2 items-start">
            {showListActions && (
              <>
                <Button
                  onClick={handleAutoPopulate}
                  variant="secondary"
                  size="sm"
                  data-testid="auto-populate-button-mobile"
                  style={SECONDARY_BTN_STYLE}
                >
                  Auto Fill
                </Button>
                <Button
                  onClick={handleReSort}
                  variant="secondary"
                  size="sm"
                  data-testid="resort-button-mobile"
                  style={SECONDARY_BTN_STYLE}
                >
                  Re-Sort
                </Button>
                <Button
                  onClick={handleClearAll}
                  variant="secondary"
                  size="sm"
                  data-testid="clear-all-button-mobile"
                  style={SECONDARY_BTN_STYLE}
                >
                  Clear All
                </Button>
              </>
            )}
            <FilterAndAddListGroup {...filtersProps} testIdSuffix="-mobile" />
          </div>
        )}
      </div>
    </header>
  );
}
